// frontend/src/components/common/FilterSheet.jsx
import React, { useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  Badge,
  Checkbox,
  ClickAwayListener,
  DialogTitle,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import MobileSheet from './MobileSheet';
import DateRangeFilter from './DateRangeFilter';
import Pill from './Pill';
import { FS, SP, TOUCH_TARGET_COARSE, TOUCH_TARGET_COARSE_QUADRAT } from '../../theme/tokens';

/**
 * FilterSheet — the one "Filters" button of a list view, opening a bottom sheet.
 *
 * @description
 *   House form for a list view's mobile utility row: ONE search field plus ONE
 *   "Filters" button that carries every filter the row cannot show inline. The
 *   button wears a count badge so the row says how many filters are active
 *   without opening the sheet.
 *
 *   The sheet body comes from either `children` (free JSX — the long tail:
 *   date ranges, free-text facets) or `criteria` (a declarative list of
 *   select/multi-select dimensions, rendered as full-width MUI selects).
 *   `criteria` exists so that a caller does NOT have to put the
 *   `BottomSheetSelect` glass pills inside the sheet: those open a drawer of
 *   their own on mobile, i.e. a sheet inside a sheet
 *   (`pages/invoicing/InvoiceList.jsx` did exactly that until 2026-10-07).
 *
 * @useWhen
 *   - A list view's mobile utility row: search + one filter button
 *   - 3+ filter dimensions that do not fit beside the search field
 *   - When the same filters exist inline on desktop (`ProductsTable` pattern):
 *     desktop keeps the inline controls, mobile folds them in here
 *
 * @dontUseFor
 *   - One or two quick toggles — those belong inline as `FilterPillRow` pills
 *   - A "what is selected" strip — that is the caller's receipt, not this button
 *   - Desktop-only toolbars with room for the controls themselves
 *
 * @related
 *   - `FilterPillRow` — the inline pill row; its `moreFiltersContent` is the
 *     older, less declarative route to the same sheet
 *   - `SearchTrigger` — the other half of the utility row
 *   - `MobileSheet` — the drawer this renders into
 *   - `DateRangeFilter` — what `criteria.kind === 'date'` renders
 *   - `ListToolbar` — renders this as `variant="popover"` and sets
 *     `countInLabel` UNCONDITIONALLY (`ListToolbar.jsx:318`). Consequence: every
 *     `ListToolbar` surface — `/products` (`ProductsTable`), `/overview`
 *     (`POOverview`), the pattern route `/design/muster` — carries the count in
 *     its visible text and therefore wears NO badge. Only the TWO direct
 *     badge-carrying callers (`pages/invoicing/InvoiceList`,
 *     `design/bestand/Makro`) keep the badge; the pattern route's own direct
 *     call is the `kal('P16')` calibration, not a surface (measured 2026-10-14
 *     with `grep -rn '<FilterSheet' frontend/src`).
 *
 * @reuseHints
 *   - Vendor list / invoice list / discovery inbox utility rows
 *   - Any table that grows a third facet
 *
 * @props
 *   label        : string   — button label (default "Filters")
 *   activeCount  : number   — active filters, shown as a badge dot when > 0
 *   title        : string   — sheet title (default "Filters")
 *   criteria     : array    — declarative criteria, drawn in the GIVEN order.
 *                            🔴 The ORDER is the CALLER's duty (concept N11.14):
 *                            sort first, then the facets in their desktop order,
 *                            then date, number, switch. The component does not
 *                            reorder — it draws the list as handed over.
 *                            Each entry:
 *                            { key, label, value, onChange, options, multiple?,
 *                              clearable?, disabled?, kind?, unit?, emptyLabel? }
 *                            `options` is `[{ value, label }]`; `multiple` takes
 *                            and returns an array; `clearable` adds an "All" row.
 *                            `kind` selects the control:
 *                            `'select'` (default) · `'date'` (→ `DateRangeFilter`,
 *                            `value` is `{ from, to }`) · `'number'` (`value` is
 *                            `{ from, to }` as **STRINGS** — the caller parses
 *                            them; `unit` only labels both fields "From (€)") ·
 *                            `'switch'` (`value` is a boolean).
 *                            🔴 `emptyLabel` (G0.12, N11.14/P17, optional) — der
 *                            BENANNTE Leerzustand eines `select`-Kriteriums ohne
 *                            Wert: statt nichts zeichnet das Feld diesen Text im
 *                            PLATZHALTER-Ton (`text.secondary`, R16 W3 — sonst
 *                            liest er sich wie ein gewaehlter Wert), „jeder
 *                            Kriterienwert ohne Öffnen lesbar". Er gilt fuer den
 *                            leeren Einzelwert UND die leere Liste (`multiple`);
 *                            ohne die Prop bleibt es beim heutigen `null`.
 *                            🔴 Das Wort waehlt der AUFRUFER (englisch). N11.14
 *                            nennt als Wert der filterlosen Zeile ausdruecklich
 *                            „Any" — P25 verbietet „All…"/„Any" als
 *                            FACETTENOPTION, NICHT den angezeigten Blatt-Wert
 *                            (R16 W3, 2026-10-13).
 *                            🔴 An UNKNOWN `kind` THROWS (house form, like
 *                            `EmptyState.reason`): a typo must not draw an empty
 *                            select and look like a working filter.
 *   children     : node     — free JSX for the long tail (rendered below the
 *                            criteria); on mobile this is where date ranges and
 *                            free-text fields go
 *   variant      : string   — `'dialog'` (default, today's form: the bottom sheet
 *                            on mobile, a plain dialog on desktop) or `'popover'`
 *                            (desktop opens a NON-MODAL popover anchored to the
 *                            button; mobile still gets the sheet). Concept
 *                            N11.17 — the popover is the new form, the sheet
 *                            stays the phone form in both variants.
 *                            🔴 `'popover'` additionally gives the surface a
 *                            header with a visible close (✕) in BOTH of its
 *                            branches, names the surface (`role="dialog"`,
 *                            `aria-modal="false"`, `aria-labelledby` on the real
 *                            heading), carries an `id` on the surface itself and
 *                            puts `aria-expanded` / `aria-haspopup="dialog"` /
 *                            `aria-controls` on the trigger — `aria-controls`
 *                            names the SURFACE, not its heading. The focus moves
 *                            into the surface when it opens (the ✕) and back to
 *                            the trigger when it closes. 🔴 The header's gap to
 *                            the content below it hangs on `alsPopover`: in the
 *                            POPOVER branch it is `SP.sm` (the `Paper` carries the
 *                            padding), in the SHEET it is `0` — the sheet's
 *                            `DialogTitle` brings its own `pb 1`, and the two sheet
 *                            forms then read alike (G0.5 condition 1). Tab WRAPS at the two
 *                            edges (last → first, first → last) — the surface is
 *                            a non-modal APG dialog, and it sits in a portal at
 *                            the END of the document order, so without the wrap
 *                            the focus would leave it. Nothing is trapped: the
 *                            page behind stays operable. The portal root carries
 *                            `role="presentation"`, because MUI's `Popper` would
 *                            otherwise wrap the dialog in `role="tooltip"`.
 *                            `'dialog'` is UNCHANGED — the three existing callers
 *                            must not change (G0-H).
 *   sort         : object   — optional sort section, rendered FIRST in the sheet
 *                            (concept §11.4). `{ value, onChange, options:
 *                            [{ value, label }], label? }`; the heading defaults
 *                            to "Sort by" and names the radio group.
 *   resultCount  : number|null|undefined — the footer's "Show … results" button.
 *                            `undefined` (default) → no footer button at all.
 *                            `null` → "Show results" WITHOUT a number: the count
 *                            is not known yet (concept §8 / OE-8 A — "unknown is
 *                            never 0"; `null` and a real `0` must not read alike).
 *                            a number → "Show 1 result" / "Show N results".
 *                            The button closes the surface.
 *   onClearAll   : function — when set, the sticky footer carries "Clear all".
 *   countInLabel : boolean  — when true, the button carries the number of active
 *                            filters in its visible text ("Filters · 2") AND in
 *                            its accessible name ("Filters, 2 active", concept
 *                            N4.6/P16). 🔴 The badge is then SUPPRESSED — the
 *                            number is already in the text, and a badge beside it
 *                            read it a third time ("Filters · 1" plus a round
 *                            "1"). 🔴 **With `countInLabel` the name is ALWAYS
 *                            set explicitly**: at `activeCount === 0` it is the
 *                            bare `label`, so the opener carries its name in
 *                            `aria-label` and not only in its text node — the
 *                            house selector list finds it by that attribute
 *                            (`selektoren.js:72`, G2a-Nachbesserung 1). The NAME
 *                            is the same either way.
 *                            🔴 WCAG 2.5.3 (2026-10-14): the FULL visible text
 *                            ("Filters · 2") is NOT a substring of the name
 *                            ("Filters, 2 active") — the separator and the word
 *                            "active" are the difference. Reviewed and ACCEPTED
 *                            (round-6 UX report, option 2): the static word
 *                            "Filters" IS in the name, so speech input still finds
 *                            the button, and the concept (N4.6/P16) prescribes
 *                            both strings. Behaviour unchanged.
 *                            Default `false`: the count stays on the badge and the
 *                            name stays bare `label` with NO `aria-label`
 *                            attribute — that is today's behaviour, and the two
 *                            direct badge-carrying callers must not change.
 *   disabled     : boolean  — 🔴 optional (G0.12, N8.5, default `false`):
 *                            sperrt den AUSLOESER. Der Desktop-Oeffner FUEHRT zu
 *                            den Facetten — bliebe er bedienbar, waere er die
 *                            „Eingabe ohne Folge", gegen die N8.5 steht. Gesperrt
 *                            dämpft er NUR über die Deckkraft; Farbe und Rand wie
 *                            bedienbar, ohne den Glasschatten wie die Pillen — die
 *                            Überschreibung gilt auf ALLEN Breiten, sichtbar wird
 *                            sie unter `down('sm')`, wo die `outlined`-Hausform
 *                            den Schatten setzt (Nachbesserung 4). Den GRUND nennt der Aufrufer sichtbar
 *                            direkt über der Leiste; ohne die Prop kein Attribut (die
 *                            fuenf Bestandsaufrufer Zeichen fuer Zeichen unveraendert).
 */
export default function FilterSheet({
  label = 'Filters',
  activeCount = 0,
  title = 'Filters',
  criteria,
  children,
  variant = 'dialog',
  sort,
  resultCount,
  onClearAll,
  countInLabel = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const knopfRef = useRef(null);
  const theme = useTheme();
  const istSchmal = useMediaQuery(theme.breakpoints.down('sm'));
  const titelId = useId();
  const sortTitelId = useId();
  const flaecheId = useId();

  // 🔴 `variant` schaltet NUR die Desktop-Form um. Auf dem Handy bleibt es in
  // beiden Faellen das Blatt (Konzept N11.17: „Desktop: derselbe Knopf oeffnet
  // ein nicht-modales Popover … mit denselben Kriterien").
  const alsPopover = variant === 'popover' && !istSchmal;

  // 🔴 Das sichtbare Schliessen (✕) haengt an der NEUEN Form, NICHT am Zweig:
  // die drei Altverbraucher fahren `variant='dialog'` und bleiben Zeichen fuer
  // Zeichen unveraendert (G0-H). Der Popover-Variante bekommt es in BEIDEN
  // ihrer Zweige — am Desktop das Popover, auf dem Handy das Blatt.
  const mitSchliessen = variant === 'popover';

  // 🔴 Der Fokus kehrt auf JEDEM Schliessweg an den Knopf zurueck (Esc,
  // Aussenklick, Fussleiste) — Konzept N11.15. Der Effekt haengt am UEBERGANG
  // offen → zu, nicht am Aufrufer.
  // 🔴 Er haengt am POPOVER-Zweig (R15 C3): der Dialog-Zweig ist ein MUI-Modal
  // (`MobileSheet` → `Dialog`/`SwipeableDrawer`), das den Fokus SELBST an den
  // Oeffner zurueckgibt — dort waere der Effekt eine zweite, unnoetige
  // Bewegung. Fuer `variant='dialog'` ist er damit ein No-op, also streng
  // additiv fuer die drei Altverbraucher.
  const warOffen = useRef(false);
  useEffect(() => {
    if (alsPopover && warOffen.current && !open) knopfRef.current?.focus();
    warOffen.current = open;
  }, [open, alsPopover]);

  // 🔴 Der Fokus geht beim OEFFNEN in die Flaeche (R16 C1). Ohne ihn bliebe er
  // auf dem Knopf: der `Popper` rendert ohne `disablePortal` an
  // `document.body`, sein Inhalt steht damit HINTER der ganzen Seite in der
  // Dokumentreihenfolge — die naechste Tab-Stelle waere das naechste
  // Bedienelement der Leiste, und die Flaeche (Selects, Radios, Textfelder,
  // Schalter) erst nach der gesamten Seite erreichbar. APG „Dialog (nicht
  // modal)" verlangt den Fokus im Dialog, WCAG 2.4.3 ist ohne ihn verletzt.
  //
  // 🔴 WARUM `autoFocus` und NICHT ein Effekt: das `Paper` liegt in einem
  // Portal, das seinen Wurzelknoten erst NACH dem Commit setzt — ein
  // `useEffect` auf `[alsPopover, open]` laeuft, waehrend `flaecheRef.current`
  // noch `null` ist, und kaeme nie ein zweites Mal (gemessen 2026-10-12: der
  // Effekt lief genau einmal, mit `knoten=null`). `autoFocus` haengt am
  // MONTAGEPUNKT des Elements und trifft damit genau den Moment, in dem die
  // Flaeche erscheint.
  //
  // Angesteuert wird das ✕ der Kopfzeile — es ist das erste Bedienelement der
  // Flaeche (die Kopfzeile steht vor dem Inhalt). Es existiert nur in der
  // `popover`-VARIANTE, und `autoFocus` haengt zusaetzlich am Zweig: das Blatt
  // auf dem Handy (`alsPopover === false`) fuehrt den Fokus selbst (MUI-Modal).
  // Der Rueckweg steht im Effekt darueber.

  // 🔴 Esc schliesst das Popover, solange es offen ist. Der Zuhoerer haengt am
  // DOKUMENT, nicht an der Flaeche: die Flaeche faengt den Fokus nicht (sie ist
  // nicht-modal), ein Escape erreicht ihren Wurzelknoten also nie. Hausform:
  // `rma/components/ShipmentStatusPopover.jsx:85-90`.
  useEffect(() => {
    if (!alsPopover || !open) return undefined;
    const aufTaste = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', aufTaste);
    return () => document.removeEventListener('keydown', aufTaste);
  }, [alsPopover, open]);

  const mitZahl = countInLabel && activeCount > 0;
  const knopfText = mitZahl ? `${label} · ${activeCount}` : label;
  const knopfName = mitZahl ? `${label}, ${activeCount} active` : undefined;

  const hatFuss = Boolean(onClearAll) || resultCount !== undefined;

  // 🔴 Drei Zustaende, nicht zwei (Konzept §8 / OE-8 A, R16 W4): „noch nicht
  // gezaehlt" und „null Treffer" duerfen NICHT dieselbe Zahl tragen.
  // `undefined` = kein Knopf · `null` = unbekannt („Show results", nie eine 0) ·
  // Zahl = Zahl, mit Singular bei 1 (R16 W7).
  const zeigenText = resultCount === undefined
    ? null
    : (Number.isFinite(resultCount)
      ? `Show ${resultCount} result${resultCount === 1 ? '' : 's'}`
      : 'Show results');

  // Die Kopfzeile der NEUEN Form: Titel als echte Ueberschrift (der Dialog-Zweig
  // bekommt sie von MUI's `DialogTitle`, hier muss sie stehen) plus das
  // sichtbare Schliessen (Konzept-Mock `tabellen-filter-suche.md:774`).
  const kopf = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: SP.tight,
        // 🔴 Der Abstand UNTER der Kopfzeile haengt an `alsPopover`, nicht an der
        // Variante (G0.5-Bedingung 1, `feat-ui2-t10-g05.ux-review.md:580-590`).
        // Er ist der Abstand zum Inhalt im POPOVER-Zweig, wo das `Paper` selbst
        // `p: SP.card` traegt und die Kopfzeile sonst direkt am ersten Abschnitt
        // klebte. Im BLATT sitzt `kopf` dagegen in einem `DialogTitle`, das seine
        // Raender aus der Blatt-Regel bezieht (`MobileSheet.jsx:44`: `pt 1.5 /
        // pb 1 / px 2.5`) — dort kaeme derselbe `mb` ZUSAETZLICH zur `pb 1` und
        // ergaebe 12 px mehr Abstand zum Inhalt als der `dialog`-Zweig.
        // Gebunden an `alsPopover` faellt er im Blatt weg und die zwei Blatt-Formen
        // sind gleich; der Popover behaelt seinen eigenen Abstand.
        // 🔴 Warum NICHT „im Blatt-Mantel `mb: 0`": die 12 px sitzen am INNEREN
        // `Box`, nicht am `DialogTitle` — eine `mb: 0` an `& .MuiDialogTitle-root`
        // traefe das Kind nicht, sondern muesste ein neuer Kindselektor in einer
        // GETEILTEN Huelle sein (jeder `MobileSheet`-Aufrufer). Eine Zeile hier,
        // am Baustein, der die Luecke erzeugt hat.
        mb: alsPopover ? SP.sm : 0,
      }}
    >
      <Typography id={titelId} component="h2" sx={{ fontSize: FS.lg, fontWeight: 600 }}>
        {title}
      </Typography>
      {mitSchliessen && (
        <IconButton
          size="small"
          aria-label="Close filters"
          // 🔴 Fokus hinein beim Oeffnen (R16 C1) — siehe Begruendung oben.
          autoFocus={alsPopover}
          onClick={() => setOpen(false)}
          sx={{ color: 'text.secondary', ...TOUCH_TARGET_COARSE_QUADRAT }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  const inhalt = (
    <>
      {sort && (
        <Box sx={{ mb: SP.card }}>
          <Typography id={sortTitelId} sx={{ fontSize: FS.sm, color: 'text.secondary', mb: SP.chip }}>
            {sort.label ?? 'Sort by'}
          </Typography>
          <RadioGroup
            value={sort.value ?? ''}
            onChange={(e) => sort.onChange?.(e.target.value)}
            aria-labelledby={sortTitelId}
          >
            {(sort.options ?? []).map((o) => (
              <FormControlLabel
                key={o.value}
                value={o.value}
                control={<Radio size="small" />}
                label={o.label}
                sx={{ ...TOUCH_TARGET_COARSE }}
              />
            ))}
          </RadioGroup>
        </Box>
      )}
      {criteria && criteria.length > 0 && (
        <Stack spacing={SP.card} sx={{ pt: SP.chip }}>
          {/* `key` wird hier ABGEZOGEN statt mitgespreizt: `{...k}` reichte das
              Feld `key` als PROP weiter, und React meldet das in jeder Probe
              („A props object containing a key prop is being spread into JSX").
              Der Baustein liest `key` nirgends — es ist reine Listenkennung. */}
          {criteria.map(({ key, ...rest }) => (
            <Kriterium key={key} {...rest} />
          ))}
        </Stack>
      )}
      {children}
      {hatFuss && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: SP.tight,
            mt: SP.sm,
            pt: SP.sm,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {onClearAll ? (
            <Button size="small" onClick={() => onClearAll()}>
              Clear all
            </Button>
          ) : (
            <Box />
          )}
          {zeigenText != null ? (
            <Button size="small" variant="contained" onClick={() => setOpen(false)}>
              {zeigenText}
            </Button>
          ) : null}
        </Box>
      )}
    </>
  );

  // 🔴 Die Tab-Schleife an den ZWEI Raendern der Flaeche (R16 Bedingung 1).
  //
  // Die Flaeche traegt `role="dialog"` und beruft sich damit auf APG „Dialog
  // (nicht modal)". Dasselbe Muster verlangt zum Tab: „Moves focus to the next
  // tabbable element inside the dialog. If focus is on the last element, focus
  // moves to the first element." Gebaut war nur die Eingangs-Haelfte (der Fokus
  // geht beim Oeffnen hinein, oben).
  //
  // 🔴 Warum die fehlende Haelfte ein Befund ist und nicht Geschmack: die
  // Flaeche steht in einem Portal an `document.body` (`Popper` ohne
  // `disablePortal`), also HINTER dem App-Baum in der Dokumentreihenfolge. Am
  // letzten Bedienelement folgt damit nichts mehr — der Fokus verlaesst das
  // Dokument bzw. springt an seinen Anfang. Und `Shift+Tab` vom ✕ trifft nicht
  // den Ausloeser, sondern das letzte tabbbare Element VOR dem Portal, also
  // irgendwo am Ende der Seite.
  //
  // 🔴 Abgefangen wird NUR an den zwei Raendern. In der Mitte muss der
  // Browser-Standard laufen, sonst waere jede Tab-Bewegung ein Sonderfall — und
  // `preventDefault` dort wuerde die Reihenfolge der Bedienelemente einfrieren.
  //
  // 🔴 WARUM KEIN `TrapFocus`/`FocusTrap`: der braechte
  // `disableEnforceFocus`/`disableAutoFocus`/`disableRestoreFocus` mit und waere
  // die halbe Modalitaet, die R16 B1 gerade abgeschafft hat („die Seite bleibt
  // bedienbar"). Zwei Zeilen Schleife, kein Fokusfang.
  const beiTasteInDerFlaeche = (e) => {
    if (e.key !== 'Tab') return;
    // Die Flaeche traegt Kinder des AUFRUFERS — die Bedienelemente stehen nicht
    // im Quelltext dieses Bausteins und werden deshalb zur Laufzeit ermittelt.
    const halt = tabbbareIn(e.currentTarget);
    if (halt.length === 0) return;
    const erstes = halt[0];
    const letztes = halt[halt.length - 1];
    // `document.activeElement` ist der GEGENSTAND der Frage („wo steht der
    // Fokus"), keine Rollen- oder Textfrage — Testing Library bietet dafuer
    // keine Abfrage.
    // eslint-disable-next-line testing-library/no-node-access -- dito
    const aktiv = document.activeElement;
    if (!e.shiftKey && aktiv === letztes) {
      e.preventDefault();
      erstes.focus();
    } else if (e.shiftKey && aktiv === erstes) {
      e.preventDefault();
      letztes.focus();
    }
  };

  // 🔴 Aussenklick schliesst das Popover UND erreicht das Element darunter: es
  // wird nichts `preventDefault`-et, der Klick geht durch. Der Ausloeser selbst
  // ist ausgenommen, sonst schloesse und oeffnete derselbe Klick. Hausform:
  // `rma/components/ShipmentStatusPopover.jsx:92-96`.
  //
  // 🔴 G11a-Nachbesserung 8 (2026-10-27) — `mouseEvent="onMouseDown"` ist
  // ZURUECK, und diesmal mit Beleg. Die Prop war in Nachbesserung 3 gesetzt und
  // in Nachbesserung 7 als „unbelegt" zurueckgenommen worden; der A/B-Lauf
  // `g11a7` (Zweigbein am Kopf `88ec3ac23d2`, 533 gruen / 6 uebersprungen /
  // GENAU 1 rot) hat die Notwendigkeit in der anderen Richtung gemessen. Der
  // eine rote Fall ist M-5.3 (`frontend/tests/media/filters.spec.ts:218`), und
  // er stirbt NICHT am Waehlen einer Option: die Probe klickt das Tag-Feld,
  // danach ist das Blatt weg — `blatt.evaluate` (:302) laeuft in den Timeout.
  // Vor der Ruecknahme sprang M-5.3 nicht bis dorthin (Skip mangels Tags), der
  // Unterschied ist also der Ruecknahme zuzuschreiben.
  //
  // Der Mechanismus ist der BROWSER, nicht dieser Baustein: `mousedown` liegt
  // im Feld (im `Paper`), `mouseup` faellt auf das inzwischen offene Menue im
  // Portal an `document.body`, und der Browser setzt das `click`-Ziel dieser
  // Geste auf den naechsten GEMEINSAMEN Vorfahren beider — `body`. Der
  // `onClick`-Zuhörer liest das als „aussen" und schliesst das Blatt mitsamt
  // dem Menue. `mousedown` hat das Problem nicht: sein Ziel ist das Element
  // unter dem Zeiger, und das lag im Blatt.
  // 🔴 Fuer alle Verbraucher des POPOVER-Zweigs heisst das: das Blatt schliesst
  // jetzt einen Wimpernschlag frueher, am `mousedown` statt am `click` —
  // derselbe Griff, nur die Haelfte des Ereignispaars. Der Ausloeser bleibt
  // ausgenommen (`knopfRef`), ein Klick daneben kommt weiterhin beim Element
  // darunter an (es wird nichts `preventDefault`-et), und kein Aufrufer haengt
  // einen eigenen `onClick` an die Flaeche (`ListToolbar`, `ListenMuster`,
  // `Makro` geprueft). Der `dialog`-Zweig ist unberuehrt (`InvoiceList`,
  // `Makro`, `ListenMuster` direkt); vier direkte Importeure, die uebrigen Listen ueber
  // `ListToolbar` mit `moreFilters`. Kalibrierung im Test: `FilterSheet.test.jsx` R15-W3
  // (Rotweg: Prop weg → der `click` auf `document.body` schliesst) und R15-W1 (Aussenklick).
  //
  // 🔴 Und die zweite Haelfte bleibt: eine Portal-Ausnahme (Klick in
  // `.MuiPopover-root`/`[role="listbox"]` gilt nicht als aussen) ist NICHT
  // noetig — `ClickAwayListener` haengt dem Kind einen eigenen `onMouseDown` an
  // (`createHandleSynthetic`, `ClickAwayListener.js:135`), der
  // `syntheticEventRef` setzt, und `handleClickAway` ruft `onClickAway` nur,
  // wenn dieses Kennzeichen falsch ist (`:99`). Ein Portal erbt den
  // synthetischen Weg des REACT-Baums — das Select-Menue ist ein React-Kind des
  // `Paper` —, also wird ein Klick in das Menue gar nicht erst als Aussenklick
  // gemeldet.
  const beiAussenklick = (e) => {
    if (knopfRef.current?.contains(e.target)) return;
    setOpen(false);
  };

  return (
    <>
      {/* 🔴 Runde-2-A/B (Nachbesserung G1a, 2026-10-14): **kein Badge, wenn
          `countInLabel` gilt.** Die Zahl steht dann schon im sichtbaren Text
          („Filters · 1") UND im Namen („Filters, 1 active") — das runde Badge
          daneben zeigte dieselbe Zahl ein DRITTES Mal (Fotobefund 1440 und 390:
          „Filters · 1" mit einem zusätzlichen „1"-Punkt am Rand). `badgeContent`
          `null` lässt MUI das Badge nicht zeichnen; die Hülle bleibt (P16:
          „der Knopf liegt in keinem Rollbehälter" hängt an ihr). */}
      <Badge badgeContent={countInLabel ? null : (activeCount || null)} color="primary">
        <Button
          ref={knopfRef}
          size="small"
          variant="outlined"
          startIcon={<TuneIcon />}
          // 🔴 G2a-Nachbesserung 1 (2026-10-14): mit `countInLabel` traegt der
          // Ausloeser seinen Namen IMMER ausdruecklich — bei `activeCount === 0`
          // ist das die nackte Beschriftung. Bis dahin war `aria-label` in genau
          // diesem Fall `undefined`, und die Hausliste der Messflaechen sucht den
          // Oeffner ueber `button[aria-label^="Filters"]`
          // (`selektoren.js:72`): auf `/overview` (Blatt-Kriterien ohne Wert,
          // `activeCount` 0) fand sie damit NICHTS, obwohl der Knopf stand — P16
          // und P17 des A/B fielen daran. Der zugaengliche NAME war nie falsch
          // (er kam aus dem Text), die Messflaeche war blind. 🔴 Nur der
          // `countInLabel`-Zweig: die uebrigen Aufrufer bleiben Zeichen fuer
          // Zeichen unveraendert (s. `countInLabel` in der Prop-Liste).
          aria-label={knopfName ?? (countInLabel ? label : undefined)}
          // 🔴 Die Zustands-Auszeichnung des Ausloesers haengt am POPOVER-Zweig
          // (R16 B2): der Dialog-Zweig wird von MUI's `Dialog` gefuehrt und
          // bleibt unveraendert — ein zusaetzliches Attribut waere eine
          // Aenderung an den drei Altverbrauchern (G0-H).
          aria-haspopup={alsPopover ? 'dialog' : undefined}
          aria-expanded={alsPopover ? open : undefined}
          // 🔴 `aria-controls` zeigt auf die FLAECHE, nicht auf ihre
          // Ueberschrift (R16 W1): benannt wird das gesteuerte Element, und das
          // ist das `Paper` mit `role="dialog"`. Auf das `<h2>` zeigte es, weil
          // dieses als einziges eine `id` trug.
          aria-controls={alsPopover && open ? flaecheId : undefined}
          // Der Ausloeser SCHALTET um — aber nur im Popover-Zweig; der
          // Dialog-Zweig oeffnet wie bisher unbedingt.
          onClick={() => setOpen((vorher) => (alsPopover ? !vorher : true))}
          // 🔴 G0.12 (N8.5, rein additiv): der „Filters"-Öffner der Leiste sperrt
          // mit, wenn der Aufrufer es verlangt. Der Desktop-Öffner FÜHRT zu den
          // Facetten (dieser Knopf ist ihr Eintritt) — bliebe er bedienbar, wäre
          // er genau die „Eingabe ohne Folge", gegen die N8.5 steht. Ohne die
          // Prop kein Attribut (`false` ist MUI-Vorgabe). Gesperrt dämpft er NUR
          // über die Deckkraft (Nachbesserung 3, R16 W2 / R15 B-3): MUI's Farb-
          // und Rand-Dämpfung ist zurückgenommen, es bleibt `opacity: 0.5` wie
          // an den Pillen der Hausform (`BottomSheetSelect.jsx:685`).
          disabled={disabled || undefined}
          sx={{
            borderRadius: '20px', px: 1.5, fontSize: FS.md,
            // 🔴 R16 W2 / R15 B-3 (Nachbesserung 3, 2026-10-13): MUI dämpft den
            // gesperrten `outlined`-Knopf zusätzlich über die Textfarbe
            // (`action.disabled`) UND den Rand (`action.disabledBackground`); die
            // Pillen der Hausform dämpfen NUR über die Deckkraft. Beide MUI-Werte
            // werden deshalb auf die des BEDIENBAREN Knopfs zurückgenommen — Text
            // `primary.main`, Rand `primary.main` halb durchsichtig (MUI `outlined`
            // primary), mobil die Glas-Kante aus `theme.js`. Nur im `disabled`-Zweig:
            // ohne die Prop fällt der leere Spread weg, der Baum ist Zeichen für
            // Zeichen der alte.
            ...(disabled ? {
              opacity: 0.5,
              '&.Mui-disabled': {
                color: 'primary.main',
                borderColor: alpha(theme.palette.primary.main, 0.5),
                // 🔴 R15 R4 W-1 (Nachbesserung 4, 2026-10-13): ohne diese Zeile
                // trägt der gesperrte Öffner weiter den Glasschatten der
                // `outlined`-Hausform; die gesperrte Pille trägt keinen
                // (`FilterPillRow.jsx:386`). 🔴 **IN `&.Mui-disabled`, NICHT auf
                // der Wurzel** — und der Grund ist die SPEZIFITÄT, nicht die
                // Einfügereihenfolge: der Glasschatten steht in
                // `MuiButton.styleOverrides.outlined` (`theme.js:702-713`,
                // `boxShadow: 'var(--glass-shadow)'` unter `breakpoints.down('sm')`),
                // das ist EINE Klasse (0,1,0); diese `sx`-Regel ist `.…Mui-disabled`
                // und damit ZWEI (0,2,0). Eine blosse `boxShadow: 'none'` auf der
                // Wurzel wäre ebenfalls EINE Klasse und entschiede über die
                // Einfügereihenfolge — darauf beruft sich diese Zeile NICHT
                // (R15 R5 W-1; die frühere Fassung nannte den Schatten fälschlich
                // „auf der Wurzel").
                // Ternärform, weil ein nacktes Literal `check_ui_tokens` als
                // `raw_shadow` zählt.
                boxShadow: disabled ? 'none' : undefined,
                [theme.breakpoints.down('sm')]: { borderColor: 'var(--glass-border)' },
              },
            } : {}),
          }}
        >
          {knopfText}
        </Button>
      </Badge>
      {alsPopover ? (
        // 🔴 NICHT-MODAL, und zwar BAUARTLICH (R16 B1): kein MUI-`Popover`, denn
        // das ist ein `Modal` — es legt einen bildschirmfuellenden Wurzelknoten
        // ueber die Seite, faengt den Fokus (`TrapFocus`) und sperrt das Rollen.
        // `Popper` + `Paper` + `ClickAwayListener` ist die Hausform fuer ein
        // Popover, das die Seite BEDIENBAR laesst
        // (`rma/components/ShipmentStatusPopover.jsx:104-128`): kein Modal, kein
        // Fokusfang, kein `aria-hidden` an Geschwistern, keine Rollensperre,
        // keine Rueckwand. Aussenklick schliesst (unten) und kommt durch, Esc
        // schliesst (Effekt oben), der Fokus kehrt an den Knopf zurueck.
        //
        // 🔴 Ohne eigene `modifiers`: popper.js bringt `flip` und
        // `preventOverflow` von sich aus mit. Die Hausform
        // (`ShipmentStatusPopover.jsx:110-113`) setzt dort zusaetzlich
        // `padding: 16` — eine nackte px-Zahl, und das UI-Token-Gate (Regel 22)
        // liest TEXT und sieht sie als rohes Abstands-Literal. Der Rand zur
        // Fensterkante ist hier ohne Belang; die Vorgaben genuegen.
        <Popper
          open={open}
          anchorEl={knopfRef.current}
          placement="bottom-start"
          // 🔴 `role="presentation"` an der Wurzel (R16 Bedingung 2). MUI's
          // `Popper` setzt dort `role="tooltip"` — gemessen am 2026-10-12 im
          // gerenderten Baum, der Pfad der Flaeche war
          // `DIV[role=dialog] < DIV[role=tooltip]`. Eine Tooltip-Rolle um einen
          // Dialog ist ein BEDEUTUNGSfehler (sie kuendigt einen Hinweistext an,
          // nicht eine bedienbare Flaeche); sie nimmt dem Dialog nichts weg, weil
          // `role="tooltip"` kein „children presentational" traegt. Die Rolle der
          // Flaeche steht am `Paper` darunter — hier wird nur die Vorgabe der
          // Wurzel ueberschrieben, sonst aendert sich nichts.
          role="presentation"
          sx={{ zIndex: theme.zIndex.modal }}
        >
          {/* 🔴 Der `ClickAwayListener` umschliesst das PAPER, nicht den inneren
              `Box` (R15 Bedingung 2, Hausform
              `components/po/PreisHerkunft.jsx:137-138`): `onClickAway` feuert
              bei einem Klick AUSSERHALB des Kindes, und alles, was zur
              sichtbaren Flaeche gehoert, aber nicht zum `Box` — der
              16-px-Randring (`p: SP.card`), der 1-px-Rahmen, die
              Bildlaufleiste — war damit „aussen": wer den Popover am Rand
              anfasste oder in ihm rollte, schloss ihn. */}
          {/* 🔴 `mouseEvent="onMouseDown"` ist der Beleg des A/B `g11a7`
              (M-5.3): mit der Vorgabe `onClick` rechnet der Browser den
              `click` der Oeffnen-Geste auf `document.body` — das Blatt gilt
              als „aussen" und schliesst mitsamt dem Select-Menue. Begruendung,
              Messung und die Wirkung fuer die Verbraucher stehen oben bei
              `beiAussenklick` (G11a-Nachbesserung 8). */}
          <ClickAwayListener onClickAway={beiAussenklick} mouseEvent="onMouseDown">
            <Paper
              id={flaecheId}
              role="dialog"
              aria-modal="false"
              aria-labelledby={titelId}
              tabIndex={-1}
              // 🔴 Die Tab-Schleife an den zwei Raendern (R16 Bedingung 1) — die
              // Begruendung steht bei `beiTasteInDerFlaeche`.
              onKeyDown={beiTasteInDerFlaeche}
              elevation={8}
              sx={{
                p: SP.card,
                minWidth: 280,
                maxWidth: 420,
                maxHeight: '70vh',
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box>
                {kopf}
                {inhalt}
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      ) : (
        <MobileSheet open={open} onClose={() => setOpen(false)}>
          {mitSchliessen ? (
            // 🔴 Die Kopfzeile der `popover`-Variante sitzt im Blatt an der
            // Stelle, an der der `dialog`-Zweig seinen `DialogTitle` hat — und
            // `MobileSheet` gibt GENAU diesem die Blatt-Raender
            // (`& .MuiDialogTitle-root: pt 1.5 / pb 1 / px 2.5`,
            // `MobileSheet.jsx:44`). Ein nacktes `Box` traf die Regel nicht und
            // stand am 0-Punkt des Blatts, waehrend der Inhalt darunter um
            // `px 2.5` eingerueckt war (R16 W5).
            //
            // 🔴 Deshalb wird `kopf` hier in einen `DialogTitle` GEHAENGT, statt
            // ihm dieselben Zahlen ein zweites Mal zu geben: dieselbe Klasse,
            // dieselbe Regel, dieselben Raender — und keine rohe Abstandszahl in
            // diesem Baum (Regel 22). Die Zahlen bleiben an EINER Stelle.
            //
            // `component="div"`, weil `DialogTitle` sonst ein `<h2>` zeichnet und
            // die Ueberschrift der Kopfzeile als zweites `<h2>` darin laege.
            // Gemessen am 2026-10-12: mit `component="div"` rendert es ein `div`
            // mit der Klasse `MuiDialogTitle-root`.
            <DialogTitle component="div">{kopf}</DialogTitle>
          ) : <DialogTitle>{title}</DialogTitle>}
          <DialogContent>{inhalt}</DialogContent>
        </MobileSheet>
      )}
    </>
  );
}

/**
 * Die Kandidaten fuer „tabbbar": alles, was von Haus aus fokussierbar ist, plus
 * jedes ausdrueckliche `tabindex`. `a[href]` und nicht `a` — ein Anker ohne
 * Ziel ist nicht fokussierbar.
 */
const TABBBAR_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]';

/**
 * Die TABBBAREN Elemente innerhalb eines Knotens, in Dokumentreihenfolge.
 *
 * 🔴 Sie werden zur LAUFZEIT ermittelt (R16 Bedingung 1): die Flaeche traegt
 * Kinder des AUFRUFERS — ihre Bedienelemente stehen nicht im Quelltext dieses
 * Bausteins. Eine fest verdrahtete Liste waere bei der ersten neuen Facette
 * falsch.
 *
 * 🔴 Was diese Auswahl NICHT kann, und warum das hier steht: sie prueft keine
 * SICHTBARKEIT (`display: none`, `visibility: hidden`, null-grosse Kaesten).
 * jsdom kann das nicht messen, und im Browser ist ein verstecktes Kind ein
 * Fehler des Aufrufers, kein Fall fuer eine zweite Weiche. Was sie sehr wohl
 * kann: `tabindex="-1"` (ueber `el.tabIndex`), `disabled`, `type="hidden"` und
 * eine RADIOGRUPPE — die zaehlt als EIN Halt, naemlich der angekreuzte Knopf
 * (APG: „moves focus to the checked radio"). Ohne diese Faltung waere das
 * „letzte Element" einer Gruppe ein Knopf, den der Browser nie anspringt, und
 * die Schleife liefe ins Leere.
 */
function tabbbareIn(knoten) {
  const halt = new Map(); // Radiogruppe (`name`) -> der Knopf, den der Browser anspringt
  const liste = [];
  Array.from(knoten.querySelectorAll(TABBBAR_SELECTOR)).forEach((el) => {
    if (el.tabIndex < 0 || el.hasAttribute('disabled') || el.getAttribute('type') === 'hidden') return;
    if (el.getAttribute('type') === 'radio' && el.name) {
      const bisher = halt.get(el.name);
      if (bisher === undefined) {
        halt.set(el.name, el);
        liste.push(el);
      } else if (el.checked && !bisher.checked) {
        // Der angekreuzte Knopf ist der Halt — er ERSETZT den ersten der Gruppe
        // an dessen Stelle, damit die Reihenfolge stimmt.
        const stelle = liste.indexOf(bisher);
        if (stelle >= 0) liste[stelle] = el;
        halt.set(el.name, el);
      }
      return;
    }
    liste.push(el);
  });
  return liste;
}

/**
 * Ein Kriterium nach seiner Art. `select` ist die Vorgabe und zeichnet genau
 * das, was der Baustein bis zum 2026-10-11 gezeichnet hat — die drei neuen
 * Arten (`date`, `number`, `switch`) kommen aus dem Konzept §3.2 und sind der
 * Grund, warum ein Aufrufer den langen Rest nicht mehr als `children` bauen muss.
 *
 * 🔴 Ein UNBEKANNTES `kind` WIRFT (R16 W8, Hausform `EmptyState.reason`): ein
 * Tippfehler (`kind: 'datum'`) zeichnete vorher still ein leeres Auswahlfeld —
 * ein Filter, der aussieht, als funktioniere er, und nichts tut. Der Fehler
 * gehoert an die Aufrufstelle, nicht in die Anzeige.
 */
function Kriterium({ kind = 'select', ...rest }) {
  if (kind === 'date') return <DateRangeFilter {...rest} />;
  if (kind === 'switch') return <Schalter {...rest} />;
  if (kind === 'number') return <Zahlbereich {...rest} />;
  if (kind === 'select') return <FilterSelect {...rest} />;
  throw new Error(
    `FilterSheet: unbekanntes criteria.kind "${kind}". Erlaubt: date, number, select, switch.`,
  );
}

/**
 * Eine Zahlspanne (Betrag, Bestand) im Blatt — Konzept §3.2 „Zahlbereich".
 * Zwei Felder mit `inputMode`, NICHT `type="number"` (pattern-library
 * §Zahlenfelder): `type="number"` bringt eine eigene Normalisierung mit und
 * steuert die Telefontastatur nur mittelbar.
 *
 * 🔴 Der Vertrag (R16 W1 / R15 W7): `value` ist `{ from, to }` mit **Strings** —
 * die Felder geben genau das weiter, was getippt wurde, und der AUFRUFER parst
 * und prueft die Grenzen. `unit` ist nur die Beschriftung („From (€)"), keine
 * Umrechnung.
 */
function Zahlbereich({ label, value, onChange, disabled = false, unit }) {
  const von = value?.from ?? '';
  const bis = value?.to ?? '';
  return (
    <Stack direction="row" sx={{ gap: SP.tight }}>
      <TextField
        size="small"
        fullWidth
        disabled={disabled}
        label={unit ? `From (${unit})` : 'From'}
        value={von}
        onChange={(e) => onChange?.({ from: e.target.value === '' ? null : e.target.value, to: value?.to ?? null })}
        inputProps={{ inputMode: 'decimal', 'aria-label': `${label} from` }}
      />
      <TextField
        size="small"
        fullWidth
        disabled={disabled}
        label={unit ? `To (${unit})` : 'To'}
        value={bis}
        onChange={(e) => onChange?.({ from: value?.from ?? null, to: e.target.value === '' ? null : e.target.value })}
        inputProps={{ inputMode: 'decimal', 'aria-label': `${label} to` }}
      />
    </Stack>
  );
}

/**
 * Ein binaerer Filter im Blatt — Konzept §3.2 „Schalter (selten)": „nie allein
 * in Z1, nie als Checkbox oder roter Chip". Der Zustand ist ein `Switch` mit
 * sichtbarer Beschriftung, damit „an" nicht allein an der Farbe haengt.
 *
 * 🔴 `...TOUCH_TARGET_COARSE` (R16 B3, N11.19/P14): das Theme traegt den
 * 44-px-Boden nur an `MuiButton`/`MuiIconButton`/`MuiToggleButton` — fuer
 * `Radio`/`Switch`/`FormControlLabel` gibt es **keinen**. Hausform fuer genau
 * diesen Fall: `design/bestand/Makro.jsx:126-128` (`FormControlLabel` mit
 * `...TOUCH_TARGET_COARSE`, direkt neben einem `FilterSheet`).
 */
function Schalter({ label, value, onChange, disabled = false }) {
  return (
    <FormControlLabel
      sx={{ ...TOUCH_TARGET_COARSE }}
      control={(
        <Switch
          size="small"
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)}
        />
      )}
      label={label}
    />
  );
}

/**
 * Der BENANNTE Leerzustand im **Platzhalter-Ton** (G0.12, N11.14/P17; R16 W3).
 *
 * 🔴 Warum ein eigener Ton: im WERT-Ton (`text.primary`) liest sich der Leerwert
 * wie ein gewählter Wert namens „Any" — die Sichtbarkeit (P17) waere erfuellt,
 * die UNTERSCHEIDBARKEIT nicht. `text.secondary` ist der Platzhalter-Ton des
 * Hauses (dieselbe Rolle, die ein leeres Feld sonst traegt).
 * 🔴 Nur wenn `emptyLabel` gesetzt ist: ohne die Prop bleibt es beim heutigen
 * `null`, der Baum jedes Bestandsaufrufers ist unveraendert (Regel 22: Token,
 * kein Literal).
 */
function Leerwert({ text }) {
  if (text == null) return null;
  return (
    <Typography component="span" sx={{ color: 'text.secondary' }}>
      {text}
    </Typography>
  );
}

/**
 * Eine Dimension im Blatt. Hausform wie der Desktop-Zweig von `BottomSheetMultiSelect`
 * (FormControl + InputLabel + Select + OutlinedInput): dieselbe Hoehe, kein zweites Blatt.
 * Die Etikettlage folgt mit E1b (`fix/ui2-formen-details`) — hier schwebt sie schon.
 */
function FilterSelect({
  label,
  value,
  onChange,
  options = [],
  multiple = false,
  clearable = false,
  disabled = false,
  emptyLabel,
}) {
  const aktuell = multiple ? (value ?? []) : (value ?? '');
  const anzeige = multiple
    ? (Array.isArray(aktuell) ? aktuell : [aktuell].filter(Boolean))
    : aktuell;

  // 🔴 `labelId` — OHNE das traegt das Feld keinen Namen. MUI verdrahtet
  // `InputLabel` und `Select` NICHT von selbst: ohne `labelId` bleibt das
  // `<label>` ohne Bezug, und `getByLabelText('Status')` findet nur das Etikett
  // („no form control was found associated to that label"), kein Bedienelement.
  //
  // 🔴 Und `id` am `Select` waere hier SCHAEDLICH, nicht zusaetzlich: MUI setzt
  // dann `aria-labelledby="<labelId> <id>"` (`SelectInput.js:461` — `buttonId`
  // ist die `id` des Anzeige-Divs), und der zugängliche Name zieht den
  // ANGEZEIGTEN WERT des Feldes mit ein. Aus „Type" wird „Type Invoice". Nur
  // `labelId` heisst: der Name ist genau die Beschriftung, unabhaengig davon,
  // was gerade gewaehlt ist — `getByRole('combobox', { name: 'Type' })` bleibt
  // damit stabil.
  const labelId = useId();

  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      {/* 🔴 Das Etikett schwebt IMMER (`shrink`). MUI leitet das Schrumpfen aus
          `value !== ''` ab; im LEEREN Feld blieb es damit IM Feld stehen und las
          sich wie ein gewaehlter Wert (Fotobefund; Hausform `VendorDetails.jsx`).
          Dieselbe Regel bringen die Desktop-Zweige von `BottomSheetSelect` und
          `BottomSheetMultiSelect` — das folgt mit E1b, `fix/ui2-formen-details`.
          Ein festes `shrink={false}` waere das Gegenteil und liesse es auch im
          geoeffneten Zustand stehen. `notched` am `Select` haelt die Kerbe offen. */}
      <InputLabel id={labelId} shrink>{label}</InputLabel>
      <Select
        labelId={labelId}
        multiple={multiple}
        label={label}
        displayEmpty
        notched
        value={aktuell}
        onChange={(e) => {
          const roh = e.target.value;
          // MUI gibt bei `multiple` ein Array zurueck; der String-Zweig traegt
          // die Form, die ein `native`-Select oder ein Auto-Fill liefern kann.
          onChange(multiple && typeof roh === 'string' ? roh.split(',') : roh);
        }}
        input={<OutlinedInput label={label} />}
        renderValue={(auswahl) => {
          // 🔴 Der LEERE Wert zeichnet NICHTS. `displayEmpty` zeigt sonst den
          // Inhalt der `value=""`-Zeile („All") AUCH im leeren Feld — ein Wert
          // im leeren Feld ist genau der Zustand, den `shrink` oben behebt. Die
          // Zeilenhoehe haelt MUI selbst (Null-Breiten-Leerzeichen; siehe
          // `BottomSheetMultiSelect.anzeigeform.test.jsx`).
          //
          // 🔴 P17 (G0.12, rein additiv): MIT `emptyLabel` zeichnet der leere
          // Wert den BENANNTEN Leerzustand statt nichts — „jeder Kriterienwert
          // ohne Öffnen lesbar" (N11.14, §15 P17). Ohne die Prop bleibt es beim
          // heutigen `null`, der Baum jedes Bestandsaufrufers ist unveraendert.
          // 🔴 Der Leerwert wird im PLATZHALTER-Ton gezeichnet (`text.secondary`,
          // `Leerwert` oben), damit „kein Wert" nicht wie ein gewählter Wert
          // aussieht (R16 W3).
          // 🔴 Das WORT waehlt der AUFRUFER (englisch). N11.14 nennt als Wert der
          // filterlosen Zeile ausdruecklich „Any" („„Any" ist dort der Wert, keine
          // Option") — P25 verbietet „All…"/„Any" als FACETTENOPTION
          // (`queryByRole('option', {name: /^all/i})`), NICHT den angezeigten
          // Leerwert des Blattes. Wer P25 auf den Blatt-Wert liest, liest ihn zu
          // weit (R16 W3, 2026-10-13).
          // 🔴 Beide Leerformen tragen den Text — die leere LISTE (`multiple`)
          // wie der leere Einzelwert. Eine Prop, die nur die Haelfte der
          // `select`-Faelle bediente, waere ein stiller Leerlauf fuer die andere.
          if (multiple) {
            const liste = Array.isArray(auswahl)
              ? auswahl
              : [auswahl].filter(Boolean);
            if (liste.length === 0) return <Leerwert text={emptyLabel} />;
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {/* 🔴 `Pill`, nicht MUI-`Chip`: das Haus hat EIN Chip-Bauteil
                    (Regel 22 §Component rules 1). Die Anzeige steht INNERHALB der
                    Combobox — eine Pille ohne `onClick` ist ein `span` und damit
                    kein zweites Bedienelement im Feld. */}
                {liste.map((v) => (
                  <Pill key={v} size="sm">
                    {options.find((o) => o.value === v)?.label ?? v}
                  </Pill>
                ))}
              </Box>
            );
          }
          if (auswahl === '' || auswahl == null) return <Leerwert text={emptyLabel} />;
          // Einfachauswahl: das Etikett der gewaehlten Option — dieselbe Anzeige,
          // die MUI ohne eigenes `renderValue` aus der `MenuItem`-Zeile zieht.
          return options.find((o) => o.value === auswahl)?.label ?? auswahl;
        }}
      >
        {clearable && <MenuItem value="">All</MenuItem>}
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {multiple && (
              <Checkbox
                checked={anzeige.includes(opt.value)}
                size="small"
                sx={{ mr: 0.5, p: 0 }}
              />
            )}
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
